import type { TProps as TLightboxProps } from '@payaca/components/lightbox/Lightbox';
import type { CustomFieldHelperType } from '@payaca/custom-fields';
import { categoryFromMimeType, type UploadCategory } from '@payaca/uploads';

export type Upload = CustomFieldHelperType['upload']['value'][number];

export type UploadTile = {
  upload: Upload;
  category: UploadCategory | undefined;
  isSafeUrl: boolean;
  /** Lightbox detail when previewable (image / video / PDF), else null. */
  detail: TLightboxProps['lightboxDetail'] | null;
  /** Position within the previewable subset the Lightbox navigates, or -1. */
  slideIndex: number;
};

/**
 * Enriches each upload with its category, URL safety, Lightbox detail and, for
 * previewable uploads, a contiguous slide index into the previewable subset.
 */
export const buildUploadTiles = (uploads: Upload[]): UploadTile[] => {
  let nextSlideIndex = 0;
  return uploads.map((upload) => {
    const category = categoryFromMimeType(upload.mimeType);
    const isSafeUrl = isHttpUrl(upload.url);
    const detail = isSafeUrl ? buildLightboxDetail(upload) : null;
    const slideIndex = detail !== null ? nextSlideIndex++ : -1;
    return { upload, category, isSafeUrl, detail, slideIndex };
  });
};

export const isHttpUrl = (url: string): boolean =>
  url.startsWith('http://') || url.startsWith('https://');

/**
 * Builds the Lightbox detail for an upload the Lightbox can preview (image,
 * video or PDF). Returns null for files the Lightbox cannot preview (e.g. a
 * Word or Excel document).
 */
export const buildLightboxDetail = (
  upload: Upload
): TLightboxProps['lightboxDetail'] | null => {
  const category = categoryFromMimeType(upload.mimeType);

  if (category === 'image') {
    return {
      type: 'image',
      src: upload.url,
      thumbnailSrc: upload.thumbnailUrl,
      title: upload.name,
      subtitle: null,
    };
  }

  if (category === 'video') {
    return {
      type: 'video',
      src: upload.url,
      videoType: upload.mimeType,
      title: upload.name,
      subtitle: null,
    };
  }

  if (upload.mimeType === 'application/pdf') {
    return { type: 'pdf', src: upload.url, title: upload.name, subtitle: null };
  }

  return null;
};
